import {
    AdapterRegistry,
    DataWarehouseLinkedService,
    type IModel,
    type ITridentResource,
    warehouseAuthTypes,
    azureDefaultAuthRefTypes,
    ReferenceAdapter,
    Credential
} from 'shell-models-lib';
import { AzureSqlDWLinkedServiceAdapter } from './azure-sql-dw-linkedservice.adapter';
import { Model } from 'app/model/backendmodel';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { servicePrincipalCredentialTypes } from 'app/model/linkedservice/dynamics.linkedservice';
import {
    akvToModel,
    akvToResource,
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from './azure-key-vault.util';
import { Metamodel } from 'app/model/base/model';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DataWarehouseLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                endpoint: '*',
                endPoint: '*',
                artifactId: '*',
                workspaceId: '*',
                tenant: __IS_FABRIC_DPE__ ? undefined : '*',
                servicePrincipalCredentialType: __IS_FABRIC_DPE__ ? undefined : '*',
                servicePrincipalId: __IS_FABRIC_DPE__ ? undefined : '*',
                servicePrincipalKey: __IS_FABRIC_DPE__ ? undefined : 'secret',
                servicePrincipalCredential: __IS_FABRIC_DPE__ ? undefined : 'secret',
                encryptedCredential: __IS_FABRIC_DPE__ ? undefined : '*',
                credential: __IS_FABRIC_DPE__ ? undefined : '*',
                authenticationType: !__IS_FABRIC_DPE__ ? '*' : undefined
            },
            parameters: '*'
        }
    }
})
export class DataWarehouseLinkedServiceAdapter extends AzureSqlDWLinkedServiceAdapter {
    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Model.DataWarehouseLinkedServiceTypeProperties => ({
        endpoint: tridentResource.extendedProperties?.QueryServiceUri as string,
        artifactId: tridentResource.objectId,
        workspaceId: tridentResource.workspaceObjectId
    });

    protected modelConstructor(): new () => DataWarehouseLinkedService {
        return DataWarehouseLinkedService;
    }

    protected model(): IModel {
        return DataWarehouseLinkedService.MODEL;
    }

    protected override async propertiesToResourceAsync(
        linkedService: DataWarehouseLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.DataWarehouseLinkedService;
        properties.type = this.model().name;

        const sharedTypeProperties = {
            endpoint: this.valueToResource(linkedService, DataWarehouseLinkedService.ENDPOINT),
            artifactId: this.valueToResource(linkedService, DataWarehouseLinkedService.ARTIFACT_ID),
            workspaceId: this.valueToResource(linkedService, DataWarehouseLinkedService.WORKSPACE_ID)
        };

        if (__IS_FABRIC_DPE__) {
            properties.typeProperties = {
                ...sharedTypeProperties
            };
        } else {
            const isSP = linkedService.authenticationMethod === warehouseAuthTypes.ServicePrincipalAuthentication;
            const isInlineSP =
                isSP && linkedService.authReferenceMethod === azureDefaultAuthRefTypes.InlineRefTypeAuthentication;

            const adfWarehouseProperties = {
                tenant: isInlineSP ? this.valueToResource(linkedService, DataWarehouseLinkedService.TENANT) : undefined,
                servicePrincipalCredentialType: isInlineSP ? linkedService.servicePrincipalCredentialType : undefined,
                servicePrincipalId: isInlineSP
                    ? this.valueToResource(linkedService, DataWarehouseLinkedService.SERVICE_PRINCIPAL_ID)
                    : undefined,
                authenticationType: linkedService.authenticationMethod,
                servicePrincipalCredential: isInlineSP
                    ? linkedService.servicePrincipalCredentialType ===
                          servicePrincipalCredentialTypes.ServicePrincipalCert && linkedService.servicePrincipalCert
                        ? akvToResource(linkedService.servicePrincipalCert, this.context)
                        : parameterizablePasswordOrAKVtoSecretBase(
                              linkedService,
                              DataWarehouseLinkedService.SERVICE_PRINCIPAL_KEY,
                              this.context
                          )
                    : undefined,
                credential:
                    linkedService.credential &&
                    ((isSP && !isInlineSP) ||
                        linkedService.authenticationMethod === warehouseAuthTypes.UserAssignedManagedIdentity ||
                        (GlobalVariables.isSamiSupportEnabled &&
                            linkedService.authenticationMethod === warehouseAuthTypes.MSIAuthentication))
                        ? (ReferenceAdapter.toResource(
                              linkedService.credential,
                              this.context
                          ) as Model.CredentialReference)
                        : undefined
            };

            properties.typeProperties = {
                ...sharedTypeProperties,
                ...adfWarehouseProperties
            };
        }

        return linkedServiceResource;
    }

    protected override async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: DataWarehouseLinkedService
    ): Promise<DataWarehouseLinkedService> {
        const properties = linkedServiceResource.properties as Model.DataWarehouseLinkedService;
        const typeProperties = properties.typeProperties as Model.DataWarehouseLinkedServiceTypeProperties;

        ValueAdapter.toModel(linkedService, DataWarehouseLinkedService.ENDPOINT, typeProperties.endpoint ?? typeProperties.endPoint);
        ValueAdapter.toModel(linkedService, DataWarehouseLinkedService.ARTIFACT_ID, typeProperties.artifactId);
        ValueAdapter.toModel(linkedService, DataWarehouseLinkedService.WORKSPACE_ID, typeProperties.workspaceId);
        linkedService.sqlAnalyticsEndpoint = typeProperties.endpoint;

        if (!__IS_FABRIC_DPE__) {
            if (typeProperties.authenticationType) {
                linkedService.authenticationMethod = typeProperties.authenticationType;
            }
            if (properties.typeProperties.servicePrincipalId) {
                ValueAdapter.toModel(
                    linkedService,
                    DataWarehouseLinkedService.SERVICE_PRINCIPAL_ID,
                    properties.typeProperties.servicePrincipalId
                );
                if (properties.typeProperties.servicePrincipalCredentialType != null) {
                    linkedService.servicePrincipalCredentialType =
                        properties.typeProperties.servicePrincipalCredentialType;
                    if (
                        linkedService.servicePrincipalCredentialType ===
                        servicePrincipalCredentialTypes.ServicePrincipalKey
                    ) {
                        parameterizableSecretBaseToModel(
                            linkedService,
                            DataWarehouseLinkedService.SERVICE_PRINCIPAL_KEY,
                            properties.typeProperties.servicePrincipalCredential,
                            this.context
                        );
                    } else if (
                        linkedService.servicePrincipalCredentialType ===
                        servicePrincipalCredentialTypes.ServicePrincipalCert
                    ) {
                        linkedService.servicePrincipalCert = akvToModel(
                            properties.typeProperties.servicePrincipalCredential as Model.SecretBase,
                            this.context
                        );
                    }
                } else {
                    parameterizableSecretBaseToModel(
                        linkedService,
                        DataWarehouseLinkedService.SERVICE_PRINCIPAL_KEY,
                        properties.typeProperties.servicePrincipalKey,
                        this.context
                    );
                }
                ValueAdapter.toModel(
                    linkedService,
                    DataWarehouseLinkedService.TENANT,
                    properties.typeProperties.tenant
                );
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
            } else if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential,
                    this.context
                );
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;
            }
        }

        return linkedService;
    }
}
